import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ImageIcon, Users, Gift, Loader2, History, Clock, CheckCircle2, Timer } from 'lucide-react';
import Button from '../ui/Button';
import ImageUploader from '../ui/ImageUploader';
import { useFinanceForm } from '../../hooks/useFinanceForm';
import { useToast } from '../../hooks/useToast';
import { rewardService, purchaseService, settingService } from '../../services';
import { getActionTypeConfig } from '../../utils/rewardConstants';
import { formatHours } from '../../utils/helpers';
import { PermissionGuard } from '../auth/PermissionGuard';
import { ButtonPermissions } from '../../types/permission';

/**
 * 编辑财务记录表单
 * 注意：账务记录一旦创建后不允许修改，只能查看和作废
 */
const FinanceFormEdit = ({ initialData = {}, onBack, onSave, onDelete }) => {
  const toast = useToast();
  
  // 账务记录创建后不允许编辑
  const isReadOnly = true;
  
  const {
    students,
    courses,
    selectedStudentId,
    selectedCourseId,
    paymentMethods,
    loading: initialLoading,
    getTodayDate,
    handleStudentChange,
    handleCourseChange,
  } = useFinanceForm(initialData);

  // 状态管理：完整的数据对象
  const [recordDetail, setRecordDetail] = useState(initialData);
  const [detailLoading, setDetailLoading] = useState(false);
  const [milestone, setMilestone] = useState('3');

  // 加载系统配置
  useEffect(() => {
    const fetchSettings = async () => {
      // 优先使用固化的快照规则
      if (recordDetail.rules_snapshot?.referral_milestone) {
        setMilestone(String(recordDetail.rules_snapshot.referral_milestone));
        return;
      }
      
      try {
        const data = await settingService.getSettings();
        const milestoneSetting = data.find(s => s.key === 'reward_referral_milestone');
        if (milestoneSetting) setMilestone(milestoneSetting.value);
      } catch (err) {
        console.error('获取系统配置失败:', err);
      }
    };
    fetchSettings();
  }, [recordDetail.rules_snapshot]);

  // 加载完整详情（包含列表API不返回的推荐和流水信息）
  useEffect(() => {
    const fetchDetail = async () => {
      if (!initialData.id) return;
      try {
        setDetailLoading(true);
        const data = await purchaseService.getById(initialData.id);
        console.log('获取账务详情成功:', data);
        setRecordDetail(data);
      } catch (err) {
        console.error('获取账务详情失败:', err);
      } finally {
        setDetailLoading(false);
      }
    };
    fetchDetail();
  }, [initialData.id]);

  // 表单字段 refs
  const dateRef = useRef(null);
  const studentIdRef = useRef(null);
  const courseIdRef = useRef(null);
  const unitPriceRef = useRef(null);
  const totalHoursRef = useRef(null);
  const amountPaidRef = useRef(null);
  const paymentMethodRef = useRef(null);
  const remarksRef = useRef(null);

  // 自动计算实付金额
  const calculateAmount = () => {
    const unitPrice = parseInt(unitPriceRef.current?.value) || 0;
    const totalHours = parseInt(totalHoursRef.current?.value) || 0;
    const amount = unitPrice * totalHours;
    if (amountPaidRef.current) {
      amountPaidRef.current.value = amount;
    }
  };

  const handleSave = () => {
    // 收集表单数据
    const formData = {
      id: initialData.id,
      purchase_date: dateRef.current?.value,
      student_id: parseInt(studentIdRef.current?.value),
      course_id: parseInt(courseIdRef.current?.value),
      unit_price: parseInt(unitPriceRef.current?.value) || 0,
      total_hours: parseInt(totalHoursRef.current?.value) || 0,
      amount_paid: parseInt(amountPaidRef.current?.value) || 0,
      payment_method: paymentMethodRef.current?.value,
      remarks: remarksRef.current?.value || '',
    };

    // 基本验证
    if (!formData.purchase_date) {
      toast.error('请选择购买日期');
      return;
    }
    if (!formData.student_id) {
      toast.error('请选择学生');
      return;
    }
    if (!formData.course_id) {
      toast.error('请选择课程');
      return;
    }
    if (!formData.amount_paid || formData.amount_paid <= 0) {
      toast.error('请输入有效的实付金额');
      return;
    }
    if (!formData.payment_method) {
      toast.error('请选择支付方式');
      return;
    }
    if (!formData.total_hours || formData.total_hours <= 0) {
      toast.error('请输入有效的总课时');
      return;
    }

    onSave(formData);
  };

  const handleForceComplete = async () => {
    try {
      if (window.confirm('强制结课将按照当前课时消耗比例，为推荐人结算赠送。不可逆操作，是否继续？')) {
        await rewardService.forceComplete({ purchase_id: initialData.id });
        toast.success('结课并结算赠送成功！');
        onBack();
      }
    } catch (err) {
      toast.error('操作失败：' + (err.message || '未知错误'));
    }
  };

  const handleTriggerVolumeBonus = async () => {
    try {
      if (window.confirm('确认手动触发该订单的达标赠送（如系统未自动发放时使用）？')) {
        await rewardService.triggerVolumeBonus({ purchase_id: initialData.id });
        toast.success('达标赠课发放成功！');
        onBack();
      }
    } catch (err) {
      toast.error('操作失败：' + (err.message || '未知错误'));
    }
  };

  // 获取不同状态的流水
  const ledgers = recordDetail.reward_ledgers || [];
  const pendingReferralLedgers = ledgers.filter(l => l.status === 'PENDING' && l.source_type === 'REFERRAL');
  const pendingVolumeBonusLedgers = ledgers.filter(l => l.status === 'PENDING' && l.source_type === 'VOLUME_BONUS');
  const completedLedgers = ledgers.filter(l => l.status === 'COMPLETED');

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      <header className="h-[56px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 -ml-1 text-[#2D3748]">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-[#2D3748]">账务查看</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
        {/* 基本信息 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden">
          {/* 购买日期 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              购买日期
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={dateRef}
              type="date" 
              className="flex-1 text-sm text-right bg-gray-50 outline-none text-[#718096] cursor-not-allowed" 
              value={(() => {
                const dateVal = recordDetail.date || recordDetail.purchase_date || recordDetail.created_at || initialData.date;
                if (dateVal) {
                  const dateOnly = String(dateVal).split(' ')[0];
                  return dateOnly.replace(/\//g, '-');
                }
                return getTodayDate();
              })()}
              readOnly
            />
          </div>

          {/* 学生姓名 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              学生姓名
              <span className="text-red-500">*</span>
            </label>
            <div className="flex-1 text-sm text-right bg-gray-50 outline-none text-[#718096] cursor-not-allowed py-1 px-2 rounded">
              {recordDetail.student_name || initialData.student_name || '未知学员'}
            </div>
            {/* 隐藏的 select 用于保持 ref 兼容性（如果 handleSave 还需要它） */}
            <select ref={studentIdRef} className="hidden" value={selectedStudentId} readOnly>
              <option value={selectedStudentId}>
                {recordDetail.student_name || initialData.student_name}
              </option>
            </select>
          </div>

          {/* 课程名称 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              课程名称
              <span className="text-red-500">*</span>
            </label>
            <div className="flex-1 text-sm text-right bg-gray-50 outline-none text-[#718096] cursor-not-allowed py-1 px-2 rounded">
              {recordDetail.course_name || initialData.course_name || '未知课程'}
            </div>
            {/* 隐藏的 select 用于保持 ref 兼容性 */}
            <select ref={courseIdRef} className="hidden" value={selectedCourseId} readOnly>
              <option value={selectedCourseId}>
                {recordDetail.course_name || initialData.course_name}
              </option>
            </select>
          </div>

          {/* 课时单价 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              课时单价
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={unitPriceRef}
              type="number" 
              placeholder="0" 
              className="flex-1 text-sm text-right bg-gray-50 outline-none text-[#718096] cursor-not-allowed" 
              value={recordDetail.unit_price || initialData.unit_price || 0}
              readOnly
            />
          </div>

          {/* 总课时 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              总课时
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={totalHoursRef}
              type="number" 
              placeholder="0" 
              className="flex-1 text-sm text-right bg-gray-50 outline-none text-[#718096] cursor-not-allowed" 
              value={recordDetail.total_hours || initialData.total_hours || 0}
              readOnly
            />
          </div>

          {/* 实付金额 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              实付金额
            </label>
            <input 
              ref={amountPaidRef}
              type="number" 
              placeholder="0" 
              className="flex-1 text-sm text-right bg-gray-50 outline-none text-[#718096] cursor-not-allowed" 
              value={recordDetail.amount_paid || initialData.amount_paid || 0}
              readOnly
            />
          </div>

          {/* 支付方式 */}
          <div className="flex items-center px-4 py-3">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              支付方式
              <span className="text-red-500">*</span>
            </label>
            <select 
              ref={paymentMethodRef}
              className="flex-1 text-sm bg-gray-50 outline-none text-[#718096] dir-rtl text-right cursor-not-allowed"
              value={recordDetail.payment_method || initialData.payment_method || ''}
              disabled
            >
              <option value="">请选择</option>
              {paymentMethods.map(method => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>

          {/* 推荐人与预期赠送 - 保持与登记一致的样式 */}
          {(initialData.has_referral || recordDetail.referral_info || (detailLoading && initialData.has_referral)) && (
            <>
              <div className="flex items-center px-4 py-3 border-t border-[#EDF2F7]">
                <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
                  推荐人
                </label>
                <div className="flex-1 text-sm text-right text-[#718096]">
                  {detailLoading ? (
                    <span className="inline-block w-20 h-4 bg-gray-100 animate-pulse rounded" />
                  ) : (recordDetail.referral_info?.referrer_name || '已失效')}
                </div>
              </div>
              <div className="flex items-center px-4 py-3 border-t border-[#EDF2F7] bg-green-50">
                <label className="w-24 text-sm font-medium text-green-700 flex items-center gap-1">
                  双向推荐赠送
                </label>
                <div className="flex-1 text-sm text-right text-green-700 font-medium">
                  {detailLoading ? (
                    <span className="inline-block w-12 h-4 bg-green-100 animate-pulse rounded" />
                  ) : formatHours(recordDetail.referral_info?.expected_reward || 0)}
                  <span className="ml-1 text-xs text-green-600">节</span>
                </div>
              </div>
            </>
          )}

          {/* 达标赠送展示 - 保持一致的样式 */}
          {(initialData.has_volume_bonus || recordDetail.volume_bonus_info || (detailLoading && initialData.has_volume_bonus)) && (
            <>
              <div className="flex items-center px-4 py-3 border-t border-[#EDF2F7]">
                <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
                  达标类型
                </label>
                <div className="flex-1 text-sm text-right text-[#718096]">
                  {detailLoading ? (
                    <span className="inline-block w-20 h-4 bg-gray-100 animate-pulse rounded" />
                  ) : (recordDetail.volume_bonus_info?.bonus_type || '购课达标')}
                </div>
              </div>
              <div className="flex items-center px-4 py-3 border-t border-[#EDF2F7] bg-purple-50">
                <label className="w-24 text-sm font-medium text-purple-700 flex items-center gap-1">
                  达标赠送课时
                </label>
                <div className="flex-1 text-sm text-right text-purple-700 font-medium">
                  {detailLoading ? (
                    <span className="inline-block w-12 h-4 bg-purple-100 animate-pulse rounded" />
                  ) : formatHours(recordDetail.volume_bonus_info?.amount || 0)}
                  <span className="ml-1 text-xs text-purple-600">节</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 支付凭证上传 */}
        <div>
          <div className="flex justify-between items-center mb-2 px-1">
            <label className="text-sm font-medium text-[#4A5568] flex items-center gap-2">
              <ImageIcon size={16} className="text-[#3182CE]" />
              支付凭证
            </label>
          </div>
          <div className="bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
            <ImageUploader
              value={recordDetail.payment_proof_urls || []}
              onChange={() => {}} // 只读模式，不允许修改
              maxFiles={3}
              disabled={true} // 编辑页面禁用上传
              uploadMeta={{
                imageType: '购买记录',
                studentId: initialData.student_id ? String(initialData.student_id) : '',
                studentName: initialData.student_name,
                courseId: initialData.course_id ? String(initialData.course_id) : '',
                courseName: initialData.course_name
              }}
            />
          </div>
        </div>

        {/* 赠送明细详情 - 整合在途与已发放 */}
        {( ledgers.length > 0 || 
            recordDetail.referral_info || 
            recordDetail.volume_bonus_info ) && (
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-2 px-1">
              <Gift size={16} className="text-purple-600" />
              <label className="text-sm font-medium text-[#4A5568]">赠送明细详情</label>
            </div>
            <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden divide-y divide-[#EDF2F7]">
              {/* 1. 在途推荐赠送 - 改为基于 Pending 流水展示 */}
              {pendingReferralLedgers.length > 0 && (
                <div className="p-4 bg-green-50/10 space-y-3">
                  <div className="flex items-center gap-1.5 px-0.5">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700 uppercase tracking-wider">
                      在途赠送
                    </span>
                    <span className="text-sm font-bold text-green-800">双向推荐赠送</span>
                  </div>
                  
                  {/* 第一类：推荐人 */}
                  {pendingReferralLedgers.filter(l => l.metadata?.role === 'referrer').map(l => (
                    <div key={l.id} className="flex justify-between items-center bg-white/50 p-2 rounded-lg border border-green-100/50">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-green-600 font-bold uppercase tracking-tight">推荐人</span>
                        <span className="text-xs text-green-800 font-medium mt-0.5">
                          {l.student_name}
                        </span>
                        <span className="text-[9px] text-green-600 mt-1">待被推荐人签到满{milestone}节入账</span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-bold text-green-700">+{formatHours(l.amount)} 节</span>
                      </div>
                    </div>
                  ))}

                  {/* 第二类：被推荐人 */}
                  {pendingReferralLedgers.filter(l => l.metadata?.role === 'referee').map(l => (
                    <div key={l.id} className="flex justify-between items-center bg-white/50 p-2 rounded-lg border border-green-100/50">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-green-600 font-bold uppercase tracking-tight">被推荐人</span>
                        <span className="text-xs text-green-800 font-medium mt-0.5">
                          {l.student_name}
                        </span>
                        <span className="text-[9px] text-green-600 mt-1">待结课后正式结算</span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-bold text-green-700">+{formatHours(l.amount)} 节</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 2. 在途达标赠送 - 改为基于 Pending 流水展示 */}
              {pendingVolumeBonusLedgers.length > 0 && (
                <div className="p-4 bg-purple-50/10 space-y-3">
                  <div className="flex items-center gap-1.5 px-0.5">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 uppercase tracking-wider">
                      在途赠送
                    </span>
                    <span className="text-sm font-bold text-purple-800">达标赠送</span>
                  </div>

                  {pendingVolumeBonusLedgers.map(l => (
                    <div key={l.id} className="flex justify-between items-center bg-white/50 p-2 rounded-lg border border-purple-100/50">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-purple-600 font-bold uppercase tracking-tight">获赠学生</span>
                        <span className="text-xs text-purple-800 font-medium mt-0.5">{l.student_name}</span>
                        <span className="text-[9px] text-purple-600 mt-1 flex items-center gap-0.5">
                           <Timer size={10} /> 待结课后正式结算
                        </span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-bold text-purple-700">+{formatHours(l.amount)} 节</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 3. 已入账流水 (Ledgers) - 只显示 COMPLETED 部分 */}
              {completedLedgers.map((ledger) => {
                const config = getActionTypeConfig(ledger.action_type);
                const isVolumeBonus = ledger.source_type === 'VOLUME_BONUS';
                const role = ledger.metadata?.role;
                const roleLabel = role === 'referrer' ? '(推荐人)' : role === 'referee' ? '(被推荐人)' : '';
                
                return (
                  <div key={ledger.id} className={`p-4 hover:bg-gray-50/50 transition-colors ${isVolumeBonus ? 'bg-purple-50/10' : ''}`}>
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${config.badgeClass}`}>
                            {config.label}
                          </span>
                          <span className={`text-sm font-bold ${config.textClass}`}>
                            {isVolumeBonus ? (ledger.metadata?.type || '购课赠送') : '双向推荐赠送'} {roleLabel}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-[#4A5568]">{ledger.student_name}</span>
                          <span className="text-[10px] text-[#A0AEC0] flex items-center gap-1">
                            <Clock size={10} /> {ledger.created_at}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`text-sm font-bold ${config.textClass}`}>+{formatHours(ledger.amount)} 节</span>
                        <span className="text-[10px] text-green-600 flex items-center gap-0.5 mt-1">
                          <CheckCircle2 size={10} /> 已入账
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 px-1 text-[10px] text-[#A0AEC0] leading-relaxed italic">
              * 在途赠送将在满足对应规则（如消课比例或结课）后自动结算入账。
            </p>
          </section>
        )}

        {/* 备注 */}
        <div>
          <label className="text-sm font-medium text-[#4A5568] mb-2 block px-1">备注</label>
          <textarea 
            ref={remarksRef}
            className="w-full bg-gray-50 border border-[#EDF2F7] rounded-xl p-3 text-sm h-24 text-[#718096] cursor-not-allowed" 
            placeholder="暂无备注"
            defaultValue={initialData.remarks}
            disabled
          />
        </div>

        {/* 时间戳信息 - 仅编辑模式显示 */}
        {initialData.createTime && (
          <div className="bg-white rounded-xl border border-[#EDF2F7] p-4 text-xs text-[#718096] space-y-2">
            <div className="flex justify-between">
              <span>创建时间</span>
              <span>{initialData.createTime}</span>
            </div>
            {initialData.updateTime && (
              <div className="flex justify-between">
                <span>修改时间</span>
                <span>{initialData.updateTime}</span>
              </div>
            )}
          </div>
        )}
      </main>

      {/* 底部按钮区 - 只读模式（账务记录创建后不允许修改）*/}
      <div className="min-h-[80px] py-2 bg-white border-t border-[#EDF2F7] px-4 flex items-center justify-between flex-shrink-0 z-10 flex-wrap gap-2">
        {/* 只有active状态的记录才显示作废按钮 */}
        {initialData.status === 'active' && initialData.payment_method !== 'VOID' && (
          <div className="flex gap-2 flex-wrap">
            <PermissionGuard permission={ButtonPermissions.purchase.delete}>
              <Button variant="danger" size="small" onClick={onDelete}>作废剩余</Button>
            </PermissionGuard>
          </div>
        )}
        {/* 已作废或冲账记录显示状态信息 */}
        {(initialData.status === 'voided' || initialData.payment_method === 'VOID') && (
          <div className="text-sm text-gray-500">
            {initialData.status === 'voided' && '该记录已作废'}
            {initialData.payment_method === 'VOID' && '这是作废冲账单据'}
          </div>
        )}
        <Button variant="secondary" onClick={onBack}>返回</Button>
      </div>
    </div>
  );
};

export default FinanceFormEdit;
